import { useEffect, useRef, useContext, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Auth from '@breville/xps-utils/core/auth';
import { localStorageProxy } from '@breville/xps-utils/core-ts/localStorageProxy';
import { logger } from '@breville/xps-utils/core-ts/logger';
import AuthContext, { AuthActionTypes } from '../../context/authContext';
import validateAuthStorage from './validateAuthStorage';

// Custom claim on the Auth0 ID token indicating active StudioPass VIP membership.
const STUDIO_PASS_VIP_CLAIM_KEY = 'isStudioPassVip';

/**
 * Hook to silently refresh and persist the Auth0 ID token.
 */
export const useRefreshIdToken = (): { fetchIdToken: () => Promise<string | null> } => {
  const { getAccessTokenSilently } = useAuth0();

  const fetchIdToken = useCallback(async () => {
    try {
      Auth.clearAuthCache();
      const { id_token } = await getAccessTokenSilently({
        ignoreCache: true,
        detailedResponse: true,
        cacheMode: 'off',
      } as any);
      if (id_token) {
        localStorageProxy.setItem('access_token', id_token);
        return id_token as string;
      }
      return null;
    } catch (err) {
      logger.error('getIdToken', { err });
      return null;
    }
  }, [getAccessTokenSilently]);

  return { fetchIdToken };
};

/**
 * Runs inside Auth0Provider on every mount.
 * Extracts the ID token, stores it in localStorage, and marks the auth flow complete.
 * Mirrors chefsteps-ui/src/library/hocs/withAuth0/AuthPageComponentWrapper.tsx
 */
const AuthPageComponentWrapper = ({
  PageComponent,
  ...props
}: {
  PageComponent: any;
  [key: string]: any;
}) => {
  const { isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const { dispatch } = useContext(AuthContext);
  const authFlowTimeoutRef = useRef<number>(0);

  const initAuthFlow = async () => {
    Auth.clearAuthCache();

    if (isAuthenticated) {
      try {
        const tokenClaims = await getIdTokenClaims();
        const token = tokenClaims?.__raw;
        if (token) {
          localStorageProxy.setItem('access_token', token);
        }
        dispatch({
          type: AuthActionTypes.SET_STUDIO_PASS_VIP,
          payload: tokenClaims?.[STUDIO_PASS_VIP_CLAIM_KEY] === true,
        });
      } catch (err) {
        logger.error('initAuthFlow:getIdTokenClaims', { err });
      } finally {
        dispatch({ type: AuthActionTypes.SET_AUTH_FLOW_COMPLETE, payload: true });
      }
    } else {
      if (validateAuthStorage()) {
        try {
          await getAccessTokenSilently();
        } catch (err) {
          // Token is expired/invalid — remove it so auth-gated code (the API
          // client interceptor, etc.) doesn't keep sending a stale token.
          logger.error('initAuthFlow:getAccessTokenSilently', { err });
          localStorageProxy.removeItem('access_token');
        }
      }
      dispatch({ type: AuthActionTypes.SET_AUTH_FLOW_COMPLETE, payload: true });
    }
  };

  useEffect(() => {
    window.clearTimeout(authFlowTimeoutRef.current);

    if (isLoading) {
      // Safeguard: force-complete if Auth0 hangs (e.g. localhost not whitelisted in Auth0 tenant)
      authFlowTimeoutRef.current = window.setTimeout(initAuthFlow, 5_000);
    } else {
      window.clearTimeout(authFlowTimeoutRef.current);
      initAuthFlow().catch(() => {
        dispatch({ type: AuthActionTypes.SET_AUTH_FLOW_COMPLETE, payload: true });
      });
    }

    return () => window.clearTimeout(authFlowTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  return <PageComponent {...props} />;
};

export default AuthPageComponentWrapper;
